import React, { useEffect, useState } from 'react'
import {
	usePostVacancyMutation,
	usePutVacancyMutation,
} from '../store/api/vacanciesApi'
import { useFormik } from 'formik'
import { PostVacancyReq, Vacancy, VacancyFormValues } from '../types'
import { useNavigate } from 'react-router-dom'
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	FormControlLabel,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import * as Yup from 'yup'

interface Props {
	data?: Vacancy
	returnToVacancy?: () => void
}

const validationSchema = Yup.object({
	title: Yup.string().required('Назва вакансії обов\'язкова'),
	company: Yup.string().required('Назва компанії обов\'язкова'),
	must_have_skills: Yup.array()
		.of(Yup.string())
		.min(1, 'Додайте хоча б один необхідний навик'),
	url: Yup.string().url('Введіть коректний URL'),
})

const VacancyForm: React.FC<Props> = ({ data, returnToVacancy }) => {
	const [mustSkill, setMustSkill] = useState('')
	const [niceSkill, setNiceSkill] = useState('')
	const [post, { isSuccess, isLoading }] = usePostVacancyMutation()
	const [put, { isSuccess: isSuccessPut, isLoading: isLoadingPut }] =
		usePutVacancyMutation()

	const formik = useFormik({
		initialValues: {
			title: data?.title || '',
			company: data?.company || '',
			location: data?.location || '',
			is_remote: data?.is_remote || false,
			salary_min: data?.salary_min ? data?.salary_min.toString() : '',
			salary_max: data?.salary_max ? data?.salary_max.toString() : '',
			salary_currency: data?.salary_currency || '',
			employment_type: data?.employment_type || '',
			schedule: data?.schedule || '',
			education_level_required: data?.education_level_required || '',
			languages_required: data?.languages_required || '',
			experience_required: data?.experience_required
				? data?.experience_required.toString()
				: '',
			original_text: data?.original_text || '',
			url: data?.url || '',
			must_have_skills: data?.must_have_skills || [],
			nice_to_have_skills: data?.nice_to_have_skills || [],
		},
		validationSchema,
		onSubmit: (values: VacancyFormValues) => {
			if (data) {
				const replaceEmptyStringsWithNull = (
					obj: VacancyFormValues,
				): Vacancy => {
					const replaced = {}

					for (const [key, value] of Object.entries(obj)) {
						if (Array.isArray(value)) {
							replaced[key] = value as string[]
						} else {
							if (
								key === 'salary_min' ||
								key === 'salary_max' ||
								key === 'experience_required'
							) {
								if (value === '') {
									replaced[key] = null
								} else {
									replaced[key] = +value
								}
							} else {
								replaced[key] = value as string
							}
						}
					}

					return replaced as Vacancy
				}

				put({
					id: data.id,
					body: replaceEmptyStringsWithNull(values),
				})
			} else {
				const cleanedValues = (): PostVacancyReq => {
					const reqData = Object.fromEntries(
						Object.entries(values).filter(([key, value]) => {
							if (Array.isArray(value)) return value.length > 0
							if (typeof value === 'boolean') return true
							return value !== '' && value !== null && value !== undefined
						}),
					)

					if (reqData.salary_min) {
						reqData.salary_min = Number(reqData.salary_min)
					}
					if (reqData.salary_max) {
						reqData.salary_max = Number(reqData.salary_max)
					}
					if (reqData.experience_required) {
						reqData.experience_required = Number(reqData.experience_required)
					}
					return reqData as PostVacancyReq
				}

				post(cleanedValues())
			}
		},
	})

	console.log('formik', formik.values)

	useEffect(() => {
		if (!isLoading && isSuccess) {
			navigate('/vacancies')
		}
	}, [isLoading])

	useEffect(() => {
		if (!isLoadingPut && isSuccessPut && returnToVacancy) {
			returnToVacancy()
		}
	}, [isLoadingPut])

	const addSkill = (
		type: 'must_have_skills' | 'nice_to_have_skills',
		value: string,
	) => {
		const trimmed = value.trim()
		if (trimmed !== '' && !formik.values[type].includes(trimmed)) {
			formik.setFieldValue(type, [...formik.values[type], trimmed])
			if (type === 'must_have_skills') setMustSkill('')
			else setNiceSkill('')
		}
	}

	const handleKeyPress = (
		e: React.KeyboardEvent,
		type: 'must_have_skills' | 'nice_to_have_skills',
	) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (type === 'must_have_skills') addSkill(type, mustSkill)
			if (type === 'nice_to_have_skills') addSkill(type, niceSkill)
		}
	}

	const removeSkill = (
		type: 'must_have_skills' | 'nice_to_have_skills',
		skill: string,
	) => {
		formik.setFieldValue(
			type,
			formik.values[type].filter((s) => s !== skill),
		)
	}

	const navigate = useNavigate()

	return (
		<Box maxWidth="700px" mx="auto" mt={4}>
			<Card>
				<CardContent>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Typography variant="h5" mr={4}>
							Форма вакансії
						</Typography>
						{!data && (
							<Button
								onClick={() => navigate('/add-from-text')}
							>
								Створити з тексту
							</Button>
						)}
					</Box>

					<form noValidate>
						<TextField
							fullWidth
							label="Назва вакансії"
							name="title"
							value={formik.values.title}
							onChange={formik.handleChange}
							margin="normal"
							error={formik.touched.title && Boolean(formik.errors.title)}
							helperText={formik.touched.title && formik.errors.title}
						/>

						<TextField
							fullWidth
							label="Компанія"
							name="company"
							value={formik.values.company}
							onChange={formik.handleChange}
							margin="normal"
							error={formik.touched.company && Boolean(formik.errors.company)}
							helperText={formik.touched.company && formik.errors.company}
						/>

						<TextField
							fullWidth
							label="Місто/Локація"
							name="location"
							value={formik.values.location}
							onChange={formik.handleChange}
							margin="normal"
						/>

						<FormControlLabel
							control={
								<Checkbox
									name="is_remote"
									checked={formik.values.is_remote}
									onChange={formik.handleChange}
								/>
							}
							label="Віддалена робота"
						/>

						<Typography variant="subtitle1" mt={2}>
							Необхідні навички
						</Typography>
						<Box display="flex" gap={1} mt={1}>
							<TextField
								fullWidth
								label="Додати навик"
								value={mustSkill}
								onChange={(e) => setMustSkill(e.target.value)}
								onKeyDown={(e) => handleKeyPress(e, 'must_have_skills')}
							/>
							<Button
								variant="contained"
								onClick={() => addSkill('must_have_skills', mustSkill)}
							>
								Додати
							</Button>
						</Box>
						<Stack direction="row" gap={1} mt={1} flexWrap="wrap">
							{formik.values.must_have_skills.map((skill, idx) => (
								<Chip
									key={idx}
									label={skill}
									onDelete={() => removeSkill('must_have_skills', skill)}
								/>
							))}
						</Stack>
						{formik.touched.must_have_skills &&
							formik.errors.must_have_skills && (
							<Typography color="error" variant="caption">
								{formik.errors.must_have_skills as string}
							</Typography>
						)}

						<Typography variant="subtitle1" mt={3}>
							Бажані навички
						</Typography>
						<Box display="flex" gap={1} mt={1}>
							<TextField
								fullWidth
								label="Додати навик"
								value={niceSkill}
								onChange={(e) => setNiceSkill(e.target.value)}
								onKeyDown={(e) => handleKeyPress(e, 'nice_to_have_skills')}
							/>
							<Button
								variant="contained"
								onClick={() => addSkill('nice_to_have_skills', niceSkill)}
							>
								Додати
							</Button>
						</Box>
						<Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
							{formik.values.nice_to_have_skills.map((skill, idx) => (
								<Chip
									variant="outlined"
									key={idx}
									label={skill}
									onDelete={() => removeSkill('nice_to_have_skills', skill)}
									sx={{ mb: 1 }}
								/>
							))}
						</Stack>

						{/* Інші поля — необов'язкові */}
						<TextField
							fullWidth
							label="Мінімальна зарплата"
							name="salary_min"
							value={formik.values.salary_min}
							onChange={(e) => {
								if (/^\d+$/.test(e.target.value)) {
									formik.handleChange(e)
								}
							}}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Максимальна зарплата"
							name="salary_max"
							value={formik.values.salary_max}
							onChange={(e) => {
								if (/^\d+$/.test(e.target.value)) {
									formik.handleChange(e)
								}
							}}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Валюта зарплати"
							name="salary_currency"
							value={formik.values.salary_currency}
							onChange={formik.handleChange}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Тип зайнятості"
							name="employment_type"
							value={formik.values.employment_type}
							onChange={formik.handleChange}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Графік роботи"
							name="schedule"
							value={formik.values.schedule}
							onChange={formik.handleChange}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Рівень освіти"
							name="education_level_required"
							value={formik.values.education_level_required}
							onChange={formik.handleChange}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Мови"
							name="languages_required"
							value={formik.values.languages_required}
							onChange={formik.handleChange}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Досвід (роки)"
							name="experience_required"
							value={formik.values.experience_required}
							onChange={(e) => {
								if (/^\d+$/.test(e.target.value)) {
									formik.handleChange(e)
								}
							}}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="Опис вакансії"
							name="original_text"
							value={formik.values.original_text}
							onChange={formik.handleChange}
							multiline
							rows={4}
							margin="normal"
						/>
						<TextField
							fullWidth
							label="URL вакансії"
							name="url"
							value={formik.values.url}
							onChange={formik.handleChange}
							margin="normal"
							error={formik.touched.url && Boolean(formik.errors.url)}
							helperText={formik.touched.url && formik.errors.url}
						/>
						{data && (
							<Button
								color="primary"
								variant="outlined"
								fullWidth
								sx={{ mt: 1 }}
								onClick={returnToVacancy}
							>
								Повернутись
							</Button>
						)}
						<Button
							loading={isLoading || isLoadingPut}
							color="primary"
							variant="contained"
							fullWidth
							sx={{ mt: 1 }}
							onClick={() => formik.handleSubmit()}
						>
							{data ? 'Зберегти' : 'Створити'}
						</Button>
					</form>
				</CardContent>
			</Card>
		</Box>
	)
}

export default VacancyForm
